import type { LinksFunction, LoaderFunctionArgs } from 'react-router';

export function headers() {
  return {
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'SAMEORIGIN',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    'X-XSS-Protection': '1; mode=block',
  };
}

import * as Sentry from '@sentry/react-router';
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
} from 'react-router';
import stylesheet from './app.css?url';
import Nav from './components/ui/Nav';
import { Toaster } from './components/ui/sonner';
import { isAdminUser } from './lib/auth/admin';
import { getSession } from './lib/auth/session';

export async function loader({ request }: LoaderFunctionArgs) {
  const session = await getSession(request);
  if (!session) return { user: null };
  return {
    user: {
      id: session.user.id,
      name: session.user.name,
      email: session.user.email,
      image: session.user.image ?? null,
      isAdmin: isAdminUser(session.user.email),
    },
  };
}

export const links: LinksFunction = () => [
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  },
  { rel: 'stylesheet', href: stylesheet },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" className="h-full">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="h-full bg-background text-foreground antialiased">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  const { user } = useLoaderData<typeof loader>();
  return (
    <div className="min-h-full flex flex-col">
      <Nav user={user} />
      <main className="flex-1">
        <Outlet />
      </main>
      <Toaster />
    </div>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();
  // Report unexpected errors (not thrown Responses like 404s) to Sentry.
  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }
  const status = isRouteErrorResponse(error) ? error.status : 500;
  const message = isRouteErrorResponse(error)
    ? error.statusText
    : error instanceof Error
      ? error.message
      : 'Unknown error';

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-muted-foreground mb-4">
          {status}
        </h1>
        <p className="text-lg text-muted-foreground">{message}</p>
        <a href="/" className="mt-6 inline-block text-primary hover:underline">
          Go home
        </a>
      </div>
    </div>
  );
}
